/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Form, Icon, Input, Radio, Spin, Table, message } from 'antd';
import { services } from '@comall-backend-builder/core';
import { RichText } from '@comall-backend-builder/components-richtext';
import * as XLSX from 'xlsx';
import { uniqBy, debounce } from 'lodash';
import { errorHandle } from '../../services';
import { hasRichTextContent } from '../../utils/rich-text';
import { RULE_DIMENSION, RULE_STATUS } from '../batch-detail-config-list/constants';
import { BatchDetailSpuSelectModal } from './spu-select-modal';
import { BatchDetailCategorySelectModal } from './category-select-modal';
import { BatchDetailSpuImportModal } from './spu-import-modal';
import './index.less';

const api = services.api;

const DETAIL_API_PATH = '/products/rich-text-rules/detail';
const ADD_API_PATH = '/products/rich-text-rules/add';
const UPDATE_API_PATH = '/products/rich-text-rules/update';

const RICH_TEXT_UPLOAD_CONFIG = {
    apiPath: '/images',
    fileName: 'fileName',
    progressCallBack: () => undefined,
};

/** 业务码判断：{ code: 200 } 成功，其他失败并展示 msg */
const assertBizSuccess = (res: any) => {
    if (res == null || typeof res.code === 'undefined') {
        return true;
    }
    if (Number(res.code) === 200) {
        return true;
    }
    message.error(res.msg || res.message || '操作失败');
    return false;
};

/** 适配 antd Form 的富文本控件（与商品「移动端详情（人工配置）」一致） */
const RichTextField: React.FC<any> = ({ value, onChange, placeholder }) => {
    return (
        <RichText
            name="richText"
            value={value || ''}
            placeholder={placeholder || '请输入移动端详情富文本'}
            uploadConfig={RICH_TEXT_UPLOAD_CONFIG}
            onChange={(html: string) => {
                if (onChange) {
                    onChange(html);
                }
            }}
        />
    );
};

/** 导出仅含 spu编码 的 Excel，并加宽行高 */
const exportSpuExcel = (fileName: string, spuCodes: string[]) => {
    const sheetData = [['spu编码'], ...spuCodes.map((code) => [code])];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet['!cols'] = [{ wch: 28 }];
    worksheet['!rows'] = sheetData.map(() => ({ hpt: 28 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

const downloadSpuTemplate = () => {
    exportSpuExcel('移动端详情SPU导入模板', ['']);
};

const exportSpuData = (list: any[]) => {
    if (!list.length) {
        message.warning('暂无已圈选 SPU');
        return;
    }
    exportSpuExcel(
        '【移动端详情已圈选SPU】',
        list.map((item) => item.spuCode || '')
    );
    message.success('导出成功');
};

const BatchDetailConfigFormCom: React.FC<any> = ({ form, params = {} }) => {
    const ruleId = params.id || '';
    const isEdit = !!ruleId;

    const [spinning, setSpinning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [spuList, setSpuList] = useState<any[]>([]);
    const [categoryList, setCategoryList] = useState<any[]>([]);
    /** 详情带回的当前规则 SPU，移除后仍视为当前单据占用，可再次勾选 */
    const [ownedSpuCodes, setOwnedSpuCodes] = useState<string[]>([]);
    const [ownedCategoryList, setOwnedCategoryList] = useState<any[]>([]);
    const [showProductSelectModal, setShowProductSelectModal] = useState(false);
    const [showCategorySelectModal, setShowCategorySelectModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const ruleDimension =
        form.getFieldValue('ruleDimension') != null
            ? form.getFieldValue('ruleDimension')
            : RULE_DIMENSION.SPU;

    const goBack = () => {
        services.navigation.goto('/product/batchDetailConfig');
    };

    const fillForm = (detail: any) => {
        const detailSpuList = detail.spuList || [];
        const detailCategoryList = detail.categoryList || [];
        setSpuList(detailSpuList);
        setOwnedSpuCodes(detailSpuList.map((item: any) => item.spuCode || '').filter(Boolean));
        setCategoryList(detailCategoryList);
        setOwnedCategoryList(detailCategoryList);
        form.setFieldsValue({
            ruleId: detail.ruleId || ruleId,
            ruleName: detail.ruleName || '',
            ruleDimension: detail.ruleDimension || RULE_DIMENSION.SPU,
            ruleStatus:
                detail.ruleStatus === RULE_STATUS.ENABLED
                    ? RULE_STATUS.ENABLED
                    : RULE_STATUS.DISABLED,
            richText: detail.richText || '',
        });
    };

    const handleGetDetail = () => {
        setSpinning(true);
        // GET /products/rich-text-rules/detail?ruleId=
        api.get({ ruleId }, { apiPath: DETAIL_API_PATH })
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    return;
                }
                // 回参：{ code, msg, data }
                const detail = res?.data || res?.result || res || {};
                fillForm(detail);
            })
            .catch(errorHandle)
            .finally(() => {
                setSpinning(false);
            });
    };

    useEffect(() => {
        if (isEdit) {
            handleGetDetail();
        } else {
            form.setFieldsValue({
                ruleDimension: RULE_DIMENSION.SPU,
                ruleStatus: RULE_STATUS.ENABLED,
                richText: '',
            });
        }
    }, []);

    const handleProductSelect = (selectedProducts: any[]) => {
        setSpuList(uniqBy(selectedProducts, 'spuCode'));
        setShowProductSelectModal(false);
        message.success(`已选择 ${selectedProducts.length} 个 SPU`);
    };

    const handleImportSuccess = (items: any[]) => {
        setSpuList((prev) => uniqBy(prev.concat(items), 'spuCode'));
        setShowUploadModal(false);
    };

    const handleRemoveSpu = (record: any) => {
        setSpuList((prev) => prev.filter((item) => item.spuCode !== record.spuCode));
    };

    const handleCategorySelect = (items: any[]) => {
        const merged = uniqBy(
            categoryList.concat(items),
            (item) => `${item.categoryCode}_${item.bigCategoryCode}`
        );
        setCategoryList(merged);
        setShowCategorySelectModal(false);
        message.success(`已添加 ${items.length} 个品类+大类组合`);
    };

    const handleRemoveCategory = (_record: any, index: number) => {
        setCategoryList((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveImpl = () => {
        if (saving) {
            return;
        }
        form.validateFields((err: any, values: any) => {
            if (err) {
                const firstErrorField = Object.values(err).find(
                    (item: any) => item && item.errors && item.errors.length
                ) as any;
                message.warning(firstErrorField?.errors?.[0]?.message || '请先把必填项填写完整');
                return;
            }

            if (values.ruleDimension === RULE_DIMENSION.SPU && !spuList.length) {
                message.warning('作用范围不能为空，请至少圈选一个 SPU');
                return;
            }
            if (values.ruleDimension === RULE_DIMENSION.CATEGORY && !categoryList.length) {
                message.warning('作用范围不能为空，请至少圈选一个品类+大类组合');
                return;
            }

            const richText = values.richText || '';
            if (!hasRichTextContent(richText)) {
                message.warning('移动端详情富文本不能为空，请输入内容');
                return;
            }

            const payload: any = {
                ruleName: (values.ruleName || '').trim(),
                ruleDimension: values.ruleDimension,
                ruleStatus: values.ruleStatus,
                richText,
            };

            // 编辑时必传
            if (isEdit) {
                payload.ruleId = ruleId;
            }

            if (values.ruleDimension === RULE_DIMENSION.SPU) {
                payload.spuList = spuList;
            } else {
                payload.categoryList = categoryList;
            }

            setSaving(true);
            const apiPath = isEdit ? UPDATE_API_PATH : ADD_API_PATH;
            api.post(payload, { apiPath })
                .then((res: any) => {
                    if (!assertBizSuccess(res)) {
                        return;
                    }
                    message.success(isEdit ? '保存成功' : '新建成功');
                    goBack();
                })
                .catch(errorHandle)
                .finally(() => {
                    setSaving(false);
                });
        });
    };

    const handleSaveImplRef = useRef(handleSaveImpl);
    handleSaveImplRef.current = handleSaveImpl;

    const handleSave = useMemo(
        () =>
            debounce(
                () => {
                    handleSaveImplRef.current();
                },
                500,
                { leading: true, trailing: false }
            ),
        []
    );

    useEffect(() => () => handleSave.cancel(), [handleSave]);

    const spuColumns = [
        {
            title: 'SPU',
            dataIndex: 'spuCode',
            key: 'spuCode',
        },
        {
            title: '商品名称',
            dataIndex: 'goodsName',
            key: 'goodsName',
            ellipsis: true,
        },
        {
            title: 'SKU 数',
            dataIndex: 'skuCount',
            key: 'skuCount',
            width: 100,
            render: (val: any) => (val != null ? val : '-'),
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
                <a className="is-danger" onClick={() => handleRemoveSpu(record)}>
                    移除
                </a>
            ),
        },
    ];

    const categoryColumns = [
        {
            title: '品类编码',
            dataIndex: 'categoryCode',
            key: 'categoryCode',
            render: (text: string) => text || '-',
        },
        {
            title: '商品品类',
            dataIndex: 'categoryName',
            key: 'categoryName',
            render: (text: string) => text || '-',
        },
        {
            title: '大类编码',
            dataIndex: 'bigCategoryCode',
            key: 'bigCategoryCode',
            render: (text: string) => text || '-',
        },
        {
            title: '商品大类',
            dataIndex: 'bigCategoryName',
            key: 'bigCategoryName',
            render: (text: string) => text || '-',
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any, index: number) => (
                <a className="is-danger" onClick={() => handleRemoveCategory(record, index)}>
                    移除
                </a>
            ),
        },
    ];

    return (
        <div className="batch-detail-config-form">
            <Spin spinning={spinning}>
                <div className="batch-detail-config-form__header">
                    <a className="batch-detail-config-form__back" onClick={goBack}>
                        <Icon type="left" />
                        返回
                    </a>
                    <div className="batch-detail-config-form__title">
                        {isEdit ? '编辑规则' : '新建规则'}
                    </div>
                    <Button type="primary" loading={saving} onClick={handleSave}>
                        保存
                    </Button>
                </div>

                <Form layout="horizontal" labelCol={{ span: 3 }} wrapperCol={{ span: 14 }}>
                    <Card title="基础信息" className="batch-detail-config-form__card">
                        {isEdit && (
                            <Form.Item label="规则ID">
                                <span>{form.getFieldValue('ruleId') || ruleId}</span>
                            </Form.Item>
                        )}
                        <Form.Item label="规则名称">
                            {form.getFieldDecorator('ruleName', {
                                rules: [{ max: 50, message: '规则名称最多 50 个字符' }],
                            })(
                                <Input
                                    placeholder="选填，不填则自动生成"
                                    maxLength={50}
                                    suffix={
                                        <span style={{ color: 'rgba(0,0,0,.45)' }}>
                                            {(form.getFieldValue('ruleName') || '').length} / 50
                                        </span>
                                    }
                                />
                            )}
                        </Form.Item>
                        <Form.Item label="规则维度">
                            {form.getFieldDecorator('ruleDimension', {
                                initialValue: RULE_DIMENSION.SPU,
                                rules: [{ required: true, message: '请选择规则维度' }],
                            })(
                                <Radio.Group disabled={isEdit}>
                                    <Radio value={RULE_DIMENSION.SPU}>SPU</Radio>
                                    <Radio value={RULE_DIMENSION.CATEGORY}>品类+大类</Radio>
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                    >
                                        创建后不可修改
                                    </span>
                                </Radio.Group>
                            )}
                        </Form.Item>
                        <Form.Item label="状态">
                            {form.getFieldDecorator('ruleStatus', {
                                initialValue: RULE_STATUS.ENABLED,
                                rules: [{ required: true, message: '请选择状态' }],
                            })(
                                <Radio.Group>
                                    <Radio value={RULE_STATUS.ENABLED}>启用</Radio>
                                    <Radio value={RULE_STATUS.DISABLED}>停用</Radio>
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                    >
                                        启用规则保存后立即参与匹配
                                    </span>
                                </Radio.Group>
                            )}
                        </Form.Item>
                    </Card>

                    <Card
                        title={
                            <span className="batch-detail-config-form__scope-title">
                                <span className="required-mark">*</span>
                                作用范围
                                <span className="batch-detail-config-form__scope-count">
                                    {ruleDimension === RULE_DIMENSION.SPU
                                        ? `已选：${spuList.length} 个 SPU`
                                        : `已选：${categoryList.length} 个品类+大类组合`}
                                </span>
                            </span>
                        }
                        className="batch-detail-config-form__card"
                    >
                        {ruleDimension === RULE_DIMENSION.SPU ? (
                            <>
                                <div className="batch-detail-config-form__toolbar">
                                    <Button
                                        type="primary"
                                        onClick={() => setShowProductSelectModal(true)}
                                    >
                                        选择 SPU
                                    </Button>
                                    <Button onClick={() => setShowUploadModal(true)}>
                                        批量导入
                                    </Button>
                                    <a onClick={downloadSpuTemplate}>下载模板</a>
                                    <a onClick={() => exportSpuData(spuList)}>下载数据</a>
                                </div>
                                <Table
                                    rowKey="spuCode"
                                    columns={spuColumns}
                                    dataSource={spuList}
                                    pagination={false}
                                    locale={{ emptyText: '请至少圈选一个 SPU' }}
                                />
                            </>
                        ) : (
                            <>
                                <div className="batch-detail-config-form__toolbar">
                                    <Button
                                        type="primary"
                                        onClick={() => setShowCategorySelectModal(true)}
                                    >
                                        添加品类+大类
                                    </Button>
                                </div>
                                <Table
                                    rowKey={(_, index) => String(index)}
                                    columns={categoryColumns}
                                    dataSource={categoryList}
                                    pagination={false}
                                    locale={{ emptyText: '请至少圈选一个品类+大类组合' }}
                                />
                            </>
                        )}
                    </Card>

                    <Card
                        title={
                            <span>
                                <span className="required-mark">*</span>
                                移动端详情富文本
                            </span>
                        }
                        className="batch-detail-config-form__card"
                    >
                        <Form.Item label="" colon={false} wrapperCol={{ span: 24 }}>
                            {form.getFieldDecorator('richText', {
                                rules: [
                                    {
                                        validator: (_rule, value, callback) => {
                                            if (!hasRichTextContent(value)) {
                                                callback('请输入移动端详情富文本');
                                                return;
                                            }
                                            callback();
                                        },
                                    },
                                ],
                            })(<RichTextField placeholder="请输入移动端详情富文本" />)}
                        </Form.Item>
                    </Card>
                </Form>
            </Spin>

            {showProductSelectModal && (
                <BatchDetailSpuSelectModal
                    visible={showProductSelectModal}
                    defaultSelected={spuList}
                    ownedSpuCodes={ownedSpuCodes}
                    onCancel={() => setShowProductSelectModal(false)}
                    onOk={handleProductSelect}
                />
            )}
            {showCategorySelectModal && (
                <BatchDetailCategorySelectModal
                    visible={showCategorySelectModal}
                    existingList={categoryList}
                    ownedList={ownedCategoryList}
                    onCancel={() => setShowCategorySelectModal(false)}
                    onOk={handleCategorySelect}
                />
            )}
            {showUploadModal && (
                <BatchDetailSpuImportModal
                    visible={showUploadModal}
                    existingList={spuList}
                    ownedSpuCodes={ownedSpuCodes}
                    onCancel={() => setShowUploadModal(false)}
                    onSuccess={handleImportSuccess}
                />
            )}
        </div>
    );
};

export const BatchDetailConfigAddEdit = Form.create()(BatchDetailConfigFormCom);
export default BatchDetailConfigAddEdit;
